import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { WindowRefService } from '../../../../services/window-ref.service';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { Subject, zip } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { EventTrackingService } from '../../../../services/event-tracking.service';
import { DomService } from '../../../shared/services/dom.service';
import { selectProfilePaperless, selectProfileResidenceResponse } from '../../../profile/store/selectors/profile.selectors';
import {
  selectCustomerAttributes,
  selectCustomerAttributesError,
} from 'src/app/feature-modules/customer-attributes/store/customer-attributes.selector';
import { selectAccounts, selectHasAccountPastDue } from 'src/app/feature-modules/accounts/store/accounts.selector';
import { AuthLinkType } from '../../../../enums/event/event-data.enum';
import { selectHubFeatureFlags, selectHubIsOptInBannerEnabled, selectIs100KLoanBannerEnabled } from 'src/app/store/selectors/feature-flag.selector';
import { FeatureFlagService } from '../../../../services/feature-flag.service';
import { OptInService } from '../../../../services/opt-in.service';
import { HubBanner } from '../../../shared/components/hub-banner/banner.model';
import { waitForSourceData } from '../../../../utils/rxjs-util';
import { selectOptIn } from '../../../opt-in/store/opt-in.selectors';
import { selectIsPaperlessBannerEligible } from '../../../../store/selectors/shared-account-opt-in.selector';
import { CustomerAttributesState } from '../../../customer-attributes/models/customer-attributes.model';
import { CardAccount, LoanAccount } from '../../../accounts/models/accounts.model';
import { AccountsService } from '../../../accounts/services/accounts.service';
import { AuthEventRepo, AuthEventType } from '../../../../enums/event/event.enum';
import {  LinkClick, LinkClickCustomerPortal, SeenBannerPlacement } from '../../../../models/event-data.model';
import { HubCardVerificationModalComponent } from '../../../shared/components/hub-card-verification-modal/hub-card-verification-modal.component';
import { selectHubOffers } from '../../../offers/store/selectors/offers.selectors';
import { Offer } from '../../../offers/models/offers.model';

type AppNotificationStatuses = 'boarded' | 'approved' | 'pending' | 'declined' | 'error';
type NotificationType =
  | 'loanPastDue'
  | 'cardPastDue'
  | 'newLimits'
  | 'paperlessEnrollment'
  | 'incomeCapture'
  | 'multi-refresh'
  | 'residenceCapture'
  | 'applicationBoarded'
  | 'applicationApproved'
  | 'applicationPending'
  | 'applicationDeclined'
  | 'applicationError';

type IconType = 'bell' | 'info' | 'check';

type IconVariant = 'neutral' | 'danger' | 'success';

interface IconConfig {
  type: IconType;
  color: string;
}

const iconColorMapping: Record<IconVariant, string> = {
  neutral: '#2f609f',
  danger: '#b91c1c',
  success: '#059669',
};

interface ApplicationData {
  notificationType: NotificationType;
  status?: AppNotificationStatuses;
  class: string;
  icon?: IconConfig;
  seenEvent?: string;
  clickEvent?: string;
}

interface Notification {
  type: NotificationType;
  seenEvent?: string;
  clickEvent?: string;
  eevTrackClickEvent?: AuthLinkType;
  event?: string;
  link?: string;
  class?: string;
  icon?: IconConfig;
}

// we are currently ignoring 'approved' | 'pending' | 'declined'
const validApplicationStatuses: AppNotificationStatuses[] = ['boarded'];

const appStatusNotificationMapping: Record<AppNotificationStatuses, Notification> = {
  boarded: {
    type: 'applicationBoarded',
    seenEvent: '[Auth] Customer Hub Person Loan Boarded Notification seen',
    class: 'notification-banner--success',
    icon: { type: 'check', color: iconColorMapping['success'] },
  },
  approved: {
    type: 'applicationApproved',
    seenEvent: '[Auth] Customer Hub Person Loan Approved Notification seen',
    clickEvent: '[Auth] Customer Hub Person Loan Approved Notification click',
    class: 'notification-banner--neutral',
  },
  pending: {
    type: 'applicationPending',
    seenEvent: '[Auth] Customer Hub Person Loan Pending Notification seen',
    class: 'notification-banner--neutral',
  },
  declined: {
    type: 'applicationDeclined',
    seenEvent: '[Auth] Customer Hub Person Loan Declined Notification seen',
    clickEvent: '[Auth] Customer Hub Person Loan Declined Notification click',
    class: 'notification-banner--declined',
  },
  error: {
    type: 'applicationError',
    class: 'notification-banner--danger',
  },
};

@Component({
  selector: 'app-notification-banner',
  templateUrl: './notification-banner.component.html',
  styleUrls: ['./notification-banner.component.scss'],
  standalone: false,
})
export class NotificationBannerComponent implements OnInit, OnDestroy {
  offer: Offer
  notifications: Notification[] = [];
  bewpBanner: HubBanner;
  AuthLinkType = AuthLinkType;
  pastDueLoanAccount: LoanAccount;
  pastDueCardAccount: CardAccount;

  isOptInBannerEnabled$ = waitForSourceData(
    [this.store.select(selectHubFeatureFlags)],
    this.store.select(selectHubIsOptInBannerEnabled)
  );
  isPaperlessBannerEligible$ = waitForSourceData(
    [this.store.select(selectOptIn), this.store.select(selectAccounts), this.store.select(selectProfilePaperless)],
    this.store.select(selectIsPaperlessBannerEligible)
  );
  hasPastDueAccount$ = this.store.select(selectHasAccountPastDue);
  cid = this.cookieService.get('cid');

  @ViewChildren('wrapperEl') wrapperEl: QueryList<any>;
  private readonly ngUnsubscribe$: Subject<boolean> = new Subject();

  constructor(
    private readonly store: Store,
    private readonly domService: DomService,
    private readonly windowRefService: WindowRefService,
    private readonly eventTrackingService: EventTrackingService,
    private readonly cookieService: CookieService,
    private readonly optInService: OptInService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly accountsService: AccountsService
  ) {}

  ngOnInit() {
    zip(
      this.store.select(selectAccounts),
      this.store.select(selectCustomerAttributes),
      this.store.select(selectProfileResidenceResponse),
      this.isOptInBannerEnabled$,
      this.isPaperlessBannerEligible$,
      this.store.select(selectHubOffers),
      this.store.select(selectIs100KLoanBannerEnabled)
    )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        ([
          accounts,
          customerAttributes,
          profileResidence,
          isOptInBannerEnabled,
          isPaperlessBannerEligible,
          offers,
          is100KLoanBannerEnabled
        ]) => {
          this.notifications = [];
          this.bewpBanner = null;
          this.pastDueLoanAccount = accounts.pastDueLoanAccount;

          // loan past due
          if (accounts.pastDueLoanAccount) {
            const loanAcctContext = accounts.loanAccounts[0];
            this.windowRefService.nativeWindow.FS.event(`[AUTH] Loan Past Due Display - pvt`);
            this.notifications.push({
              type: 'loanPastDue',
              link: loanAcctContext?.url,
              class: 'notification-banner--danger',
              icon: { type: 'bell', color: iconColorMapping['danger'] },
            });
          }

          // card past due
          if (accounts.hasPastDueCardAccount) {
            this.windowRefService.nativeWindow.FS.event(`[AUTH] Credit Card Past Due Display - pvt`);
            this.pastDueCardAccount =
              accounts.cardAccounts.find(card => card.accountStatus?.toLowerCase() === 'past due') ?? accounts.cardAccounts[0];
            this.notifications.push({
              type: 'cardPastDue',
              class: 'notification-banner--danger',
              icon: { type: 'bell', color: iconColorMapping['danger'] },
            });
          }

          // past due notifications have priority over all other notifications
          if (this.notifications.length) {
            return;
          }

          this.offer = offers ? offers.find(offer => offer.offerUrl.includes('y_n_100k')) : null;

          // new limits to 100k notification
          if (this.offer && is100KLoanBannerEnabled) {
            const newLimitsNotification: Notification = {
              type: 'newLimits',
              link: `/offers`,
              class: 'notification-banner--neutral',
              icon: { type: 'bell', color: iconColorMapping['neutral'] },
            };
            this.notifications.push(newLimitsNotification);
            this.fire100kBannerSeenEvent(this.offer);
            return;
          }

          if (isOptInBannerEnabled) {
            // return and display separate component in template
            this.bewpBanner = { type: 'optIn' };
            return;
          }

          if (isPaperlessBannerEligible) {
            this.bewpBanner = {
              type: 'paperlessEnrollment',
              href: `${accounts.loanAccounts[0]?.url}&destination=paperless-profile`,
            };
            return;
          }

          // app notification
          const appNotification = this.getApplicationNotification(customerAttributes);
          if (appNotification) {
            this.notifications.push(appNotification);

            if (appNotification?.seenEvent) {
              this.windowRefService.nativeWindow.FS.event(appNotification.seenEvent);
            }
            return;
          }

          //profile update notifications
          const profileNotification = this.getProfileUpdateNotification(
            customerAttributes?.customerProfileIncome?.isCustomerIncomeAttributeRefreshRequired,
            profileResidence
          );
          if (profileNotification) {
            this.notifications.push(profileNotification);
          }
        }
      );

    // cas data error
    this.store
      .select(selectCustomerAttributesError)
      .pipe(
        filter((val) => val !== null),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(() => {
        this.notifications.push(appStatusNotificationMapping['error']);
      });
  }

  fire100kBannerSeenEvent(offer: Offer){
    this.eventTrackingService.trackEvent<SeenBannerPlacement>(AuthEventType.SEEN_BANNER_PLACEMENT, {
      customerPortalDisplayType: '100k Unsecured Loan Banner',
      customerPortalDisplayText: 'New limits! Get up to $100k for your bigger financial goals.',
      siteSection: 'hub',
      subProduct: offer.subProductType,
      product: offer.productType,
      crossBuyOfferType: offer.offerType,
      eventRepo: AuthEventRepo.CustomerPortal,
    })
  }

  //Notification Builders
  getProfileUpdateNotification(isIncomeRefreshRequired, profileResidence): Notification | null {
    const currentDate = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(currentDate.getFullYear() - 1);

    const residenceRefreshRequired = profileResidence
      ? profileResidence.some((residence) => new Date(residence.insertTs) < oneYearAgo) || profileResidence.length === 0
      : false;

    if (isIncomeRefreshRequired && residenceRefreshRequired) {
      return {
        type: 'multi-refresh',
        link: `profile/income`,
        class: 'notification-banner--neutral',
        eevTrackClickEvent: AuthLinkType.NAV_TO_PROFILE,
        icon: { type: 'bell', color: iconColorMapping['neutral'] },
      };
    }

    if (isIncomeRefreshRequired) {
      return {
        type: 'incomeCapture',
        link: `profile/income`,
        class: 'notification-banner--neutral',
        eevTrackClickEvent: AuthLinkType.NAV_TO_INCOME,
        icon: { type: 'bell', color: iconColorMapping['neutral'] },
      };
    }

    if (residenceRefreshRequired) {
      return {
        type: 'residenceCapture',
        link: `profile/residence`,
        class: 'notification-banner--neutral',
        eevTrackClickEvent: AuthLinkType.NAV_TO_RESIDENCE,
        icon: { type: 'bell', color: iconColorMapping['neutral'] },
      };
    }

    return null;
  }



  onNewLimitsClick(): void {
    // send EEV with to-be-determined CPID when clicked
    this.eventTrackingService.trackEvent<LinkClickCustomerPortal>(AuthEventType.LINK_CLICK, {
      customerPortalClickLinkText: 'View offers',
      customerPortalClickLinkType: AuthLinkType.NAV_TO_OFFERS,
      crossBuyOfferType: this.offer.offerType,
      customerPortalDisplayType: '100k Unsecured Loan Banner',
      siteSection: 'hub',
      eventRepo: AuthEventRepo.CustomerPortal,
    });
  }

  getApplicationNotification(customerAttributes: CustomerAttributesState): Notification | null {
    const hasBannerNotificationType = customerAttributes?.mostRecentLoanNotificationType === 'Banner';
    const hasEnabledAppStatus = validApplicationStatuses.includes(
      customerAttributes?.mostRecentLoanStatus?.toLowerCase() as AppNotificationStatuses
    );
    const hasAppNotification = hasBannerNotificationType && hasEnabledAppStatus;
    let notification = appStatusNotificationMapping[customerAttributes?.mostRecentLoanStatus?.toLowerCase()];

    if (!hasAppNotification || !notification) {
      return null;
    }
    return {
      ...notification,
      link: customerAttributes?.loanAcquisitionsPurl,
    };
  }

  //Click/Event handlers
  onLoanPastDueNotificationClick() {
    const FS_EVENT = `[AUTH] Loan Past Due Link clicked`;
    this.windowRefService.nativeWindow.FS.event(FS_EVENT);
    this.eventTrackingService.trackEnterpriseEvent({
      eventName: 'redirectFromPastDueLoanBanner',
      cid: this.cookieService.get('cid'),
    });
  }

  onCardPastDueNotificationClick() {
    const FS_EVENT = `[AUTH] Card Past Due Link clicked`;
    this.windowRefService.nativeWindow.FS.event(FS_EVENT);
    this.eventTrackingService.trackEnterpriseEvent({
      eventName: 'redirectFromPastDueCardBanner',
      cid: this.cookieService.get('cid'),
    });
    this.accountsService.navigateToCreditCard(this.pastDueCardAccount, HubCardVerificationModalComponent);
  }

  fireApplicationClickEvent(clickEvent?: string) {
    if (!clickEvent) {
      return;
    }
    this.windowRefService.nativeWindow.FS.event(clickEvent);
  }

  closeNotification(e, i) {
    const targetElement = this.wrapperEl.filter((element, index) => index === i);
    this.domService.collapseSection(targetElement[0].nativeElement);
  }

  acceptOptIn(event: boolean) {
    if (event) {
      this.optInService
        .acceptOptIn()
        .pipe(take(1))
        .subscribe({ error: (error) => console.error(error) });
    } else {
      this.featureFlagService
        .updateFeatureFlag([
          {
            featureFlagName: 'AWEE395ExpressLaneOptInBanner',
            enabled: false,
          },
        ])
        .pipe(take(1))
        .subscribe({ error: (error) => console.error(error) });
    }
  }

  dismissPaperlessBanner() {
    this.optInService.dismissPaperlessBanner().pipe(take(1)).subscribe();
  }

  triggerMakePaymentEev() {
    this.eventTrackingService.trackEvent<LinkClick>(AuthEventType.LINK_CLICK, {
      authClickLinkText: 'Make a payment now',
      authClickLinkType: AuthLinkType.NAV_TO_LOAN_PAYMENT,
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next(null);
    this.ngUnsubscribe$.complete();
  }
}
